import { useTranslation } from 'react-i18next';

import type { PatientOverviewResponse } from '../../api/types';
import { Columns, type Column } from '../../components/charts/Columns';
import { RankedBars, type RankedBar } from '../../components/charts/RankedBars';
import { percent } from '../../components/charts/buckets';
import { count } from '../../i18n/format';

/** Six months or more since the last visit, or never treated: the patients worth a call. */
const LAPSING = new Set(['WITHIN_A_YEAR', 'OVER_A_YEAR', 'NEVER']);

/**
 * The patients screen's charts (CR-33): the clinic's patients in counts, never in names.
 *
 * Three questions a clinic manager asks about the patient book, each answered by one chart. Is it
 * growing — new records a month for a year. Are they coming back — how long since each patient's
 * last completed visit, where the long-ago and never-treated bands are the call list a clinic
 * usually does not know it has. Are they reliable — how many appointments each has missed, where
 * two or more is the patient to confirm the day before. Where the records came from sits beside
 * them, because it says how much the patient app is doing yet.
 *
 * Age and gender are not here on purpose: the desk's ten-second form does not ask for either
 * (FR-102), so a chart of them would be a chart of "not recorded".
 */
export function PatientsOverview({ overview }: { readonly overview: PatientOverviewResponse }) {
  const { t, i18n } = useTranslation();

  const total = overview.total ?? 0;
  const months = overview.newByMonth ?? [];
  const thisMonth = months[months.length - 1]?.count ?? 0;
  const lastMonth = months[months.length - 2]?.count ?? 0;
  const visits = overview.byLastVisit ?? [];
  const recent = visits
    .filter((row) => row.recency === 'WITHIN_30_DAYS' || row.recency === 'WITHIN_90_DAYS')
    .reduce((running, row) => running + (row.count ?? 0), 0);

  const monthName = (month: string, style: 'short' | 'long') =>
    new Intl.DateTimeFormat(i18n.language, {
      month: style,
      ...(style === 'long' ? { year: 'numeric' as const } : {}),
      timeZone: 'UTC',
      numberingSystem: 'latn',
    }).format(new Date(`${month}-01T00:00:00Z`));

  const columns: Column[] = months.map((row, index) => ({
    key: row.month ?? String(index),
    label: monthName(row.month ?? '', 'short'),
    description: t(index === months.length - 1 ? 'overview.patients.newSoFar' : 'overview.patients.newIn', {
      month: monthName(row.month ?? '', 'long'),
      count: row.count ?? 0,
    }),
    segments: [{ value: row.count ?? 0, colour: 'var(--chart-1)' }],
  }));

  const recency: RankedBar[] = visits.map((row) => ({
    key: row.recency ?? '',
    label: t(`overview.patients.recency.${row.recency ?? 'NEVER'}`),
    value: row.count ?? 0,
    tone: LAPSING.has(row.recency ?? '') ? 'alert' : undefined,
  }));
  const missed: RankedBar[] = (overview.byNoShows ?? []).map((row) => ({
    key: String(row.noShows ?? 0),
    label: t(`overview.patients.missed.${Math.min(row.noShows ?? 0, 3)}`),
    value: row.count ?? 0,
    tone: (row.noShows ?? 0) >= 2 ? 'alert' : undefined,
  }));
  const sources: RankedBar[] = (overview.bySource ?? []).map((row) => ({
    key: row.source ?? '',
    label: t(`patients.sources.${row.source ?? 'WALK_IN'}`),
    value: row.count ?? 0,
  }));

  return (
    <div className="overview">
      <section className="measures" aria-label={t('overview.patients.strip')}>
        <div className="measure measure--lead">
          <span className="measure__label">{t('overview.patients.total')}</span>
          <span className="measure__value">{count(total)}</span>
          <span className="measure__note">{t('overview.patients.totalNote')}</span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.patients.newThisMonth')}</span>
          <span className="measure__value">{count(thisMonth)}</span>
          <span className="measure__note">{t('overview.patients.lastMonth', { count: lastMonth })}</span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.patients.recent')}</span>
          <span className="measure__value">{count(recent)}</span>
          <span className="measure__note">{t('overview.shareOfPatients', { share: percent(recent, total) })}</span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.patients.onTheApp')}</span>
          <span className="measure__value">{count(overview.withAppAccount ?? 0)}</span>
          <span className="measure__note">
            {t('overview.shareOfPatients', { share: percent(overview.withAppAccount ?? 0, total) })}
          </span>
        </div>
      </section>

      <div className="overview-grid">
        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.patients.newByMonth')}</h2>
            <p className="muted">{t('overview.patients.newByMonthNote')}</p>
          </div>
          <Columns columns={columns} label={t('overview.patients.newByMonth')} />
        </section>

        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.patients.lastVisit')}</h2>
            <p className="muted">{t('overview.patients.lastVisitNote')}</p>
          </div>
          <RankedBars rows={recency} />
        </section>

        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.patients.missedHeading')}</h2>
            <p className="muted">{t('overview.patients.missedNote')}</p>
          </div>
          <RankedBars rows={missed} />
        </section>

        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.patients.sources')}</h2>
            <p className="muted">{t('overview.patients.sourcesNote')}</p>
          </div>
          <RankedBars rows={sources} />
        </section>
      </div>
    </div>
  );
}
